import React, { useState, useEffect } from 'react';
import {
  Shield, FileText, Database, Lock, Users, Bot,
  Server, AlertTriangle, Accessibility, ChevronDown,
  ChevronRight, CheckCircle, Globe, Trash2, Key,
  Activity, BarChart3, ArrowRight, Loader2
} from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const getToken = () => localStorage.getItem('mosaic_session_token');
const authH = () => ({ headers: { Authorization: `Bearer ${getToken()}` } });

const Section = ({ icon: Icon, title, color, children, defaultOpen = false }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="bg-white border border-gray-200 rounded-lg overflow-hidden" data-testid={`erma-${title.toLowerCase().replace(/\s+/g,'-')}`}>
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors">
        <div className="flex items-center gap-3">
          <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${color}`}><Icon size={16} /></div>
          <span className="font-bold text-gray-800 text-sm">{title}</span>
          <span className="text-[10px] font-bold bg-green-100 text-green-700 px-2 py-0.5 rounded">READY</span>
        </div>
        {open ? <ChevronDown size={16} className="text-gray-400" /> : <ChevronRight size={16} className="text-gray-400" />}
      </button>
      {open && <div className="px-4 pb-4 border-t border-gray-100 pt-3 space-y-3">{children}</div>}
    </div>
  );
};

const Row = ({ label, value, highlight }) => (
  <div className="flex justify-between items-start py-1.5 border-b border-gray-50 last:border-0">
    <span className="text-xs text-gray-500 w-2/5">{label}</span>
    <span className={`text-xs text-right w-3/5 ${highlight ? 'font-bold text-green-700' : 'text-gray-800'}`}>{value}</span>
  </div>
);

const DT = ({ headers, rows }) => (
  <div className="border border-gray-200 rounded-lg overflow-hidden text-xs">
    <table className="w-full">
      <thead><tr className="bg-gray-50">{headers.map((h,i) => <th key={i} className="text-left p-2 font-bold text-gray-600 text-[10px] uppercase">{h}</th>)}</tr></thead>
      <tbody>{rows.map((row,ri) => <tr key={ri} className={ri%2===0?'bg-white':'bg-gray-50/50'}>{row.map((c,ci) => <td key={ci} className="p-2 text-gray-700">{c}</td>)}</tr>)}</tbody>
    </table>
  </div>
);

const ERMAComplianceTab = () => (
  <div className="space-y-5" data-testid="erma-compliance-tab">
    <div>
      <h2 className="text-lg font-bold text-gray-800" style={{ fontFamily: 'Outfit, sans-serif' }}>ERMA Compliance Center</h2>
      <p className="text-xs text-gray-500">NYCPS Data Privacy & Security Compliance — all artifacts for ERMA/OneTrust approval</p>
    </div>
    <div className="bg-white border-2 border-green-200 rounded-lg p-5">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-3">
          <Shield size={24} className="text-green-600" />
          <div><h3 className="font-bold text-gray-800 text-sm">ERMA Readiness Status</h3><p className="text-[10px] text-gray-500">NYCPS Data Privacy and Security Compliance Process</p></div>
        </div>
        <a href="/api/blm-downloads/MOSAIC_ERMA_Compliance_White_Paper.pdf" target="_blank" rel="noopener noreferrer"
           className="inline-flex items-center gap-1.5 bg-[#002FA7] hover:bg-[#002FA7]/90 text-white text-xs font-bold px-4 py-2 rounded-lg transition-colors" data-testid="erma-download-whitepaper">
          <FileText size={14} /> Download ERMA White Paper (PDF)
        </a>
      </div>
      <div className="grid grid-cols-6 gap-2">
        {['OneTrust Assessment','DPA','IT Security Review','Cloud Review','Automated Processing Disclosure','Parent Bill of Rights'].map((l,i) => (
          <div key={i} className="bg-green-50 border border-green-200 rounded-lg p-2 text-center">
            <CheckCircle size={16} className="mx-auto text-green-600 mb-1" /><div className="text-[9px] font-bold text-green-700">{l}</div>
          </div>
        ))}
      </div>
    </div>

    <Section icon={Database} title="Data Elements Inventory" color="bg-blue-100 text-blue-700" defaultOpen>
      <DT headers={['Data Element','Category','Purpose','PII?','Retention']} rows={[
        ['Student full name','Directory','Account ID, reports','Yes','License + 90d'],
        ['Student email/username','Directory','Authentication','Yes','License + 90d'],
        ['Password (bcrypt hash)','Auth','Login — never plaintext','No (hashed)','License + 90d'],
        ['Grade level','Education','Assessment assignment','Yes','License + 90d'],
        ['School/class assignment','Education','Multi-tenancy, grouping','Yes','License + 90d'],
        ['Assessment responses','Ed Record','WIDA scoring, growth','Yes','License + 90d'],
        ['WIDA scores (1.0-6.0)','Ed Record','Proficiency reporting','Yes','License + 90d'],
        ['Speaking audio (temp)','Ed Record','STT for Automated rubric support','Yes','Deleted < 5min'],
        ['Writing responses','Ed Record','WIDA Writing scoring','Yes','License + 90d'],
        ['Teacher/Admin name+email','Directory','Account management','Yes','License + 90d'],
        ['IP address','Technical','Exam integrity, logs','Yes','1 year'],
        ['Session tokens','Auth','Auth state','No','24hr expiry'],
      ]} />
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mt-2">
        <p className="text-[10px] text-blue-700"><strong>NOT collected:</strong> SSN, home address, phone, biometrics, health records, disciplinary records, lunch status, immigration status.</p>
      </div>
    </Section>

    <Section icon={Server} title="Data Storage & Architecture" color="bg-indigo-100 text-indigo-700">
      <Row label="Database" value="MongoDB Atlas (cloud-hosted)" />
      <Row label="Location" value="US-East (Virginia, AWS us-east-1)" highlight />
      <Row label="Data Residency" value="United States only — no international transfer" highlight />
      <Row label="Encryption at Rest" value="AES-256 (MongoDB Atlas)" />
      <Row label="Encryption in Transit" value="TLS 1.2+ all connections" />
      <Row label="Hosting" value="Emergent Platform (US-based)" />
      <Row label="CDN/DNS" value="Cloudflare (US — routing only, no data storage)" />
      <Row label="Backups" value="Continuous backup, 30-day retention, US-East" />
      <Row label="Network" value="DB accessible only from app servers via private connection" />
    </Section>

    <Section icon={Lock} title="Security Controls" color="bg-red-100 text-red-700">
      <DT headers={['Control','Implementation','Standard']} rows={[
        ['Authentication','bcrypt (cost 12), 24hr session tokens','NIST 800-63B'],
        ['RBAC','5 roles: student→super_admin, API enforcement','Least privilege'],
        ['Data Isolation','school_id/district_id boundaries','FERPA'],
        ['Brute Force','Lockout after 5 fails (15min cooldown)','OWASP'],
        ['Input Validation','Server-side, parameterized queries','OWASP Top 10'],
        ['Transport','TLS 1.2+, HSTS headers','PCI DSS'],
        ['Audit Logging','All auth, access, admin actions logged','SOC 2'],
        ['Exam Integrity','Tab-switch detection, copy blocking','Test security'],
      ]} />
    </Section>

    <Section icon={Bot} title="Automated Processing Disclosure Statement" color="bg-purple-100 text-purple-700">
      <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 mb-3">
        <p className="text-xs text-purple-800 font-bold mb-1">NYCPS Automated Processing Compliance</p>
        <p className="text-xs text-purple-700">MOSAIC uses automated content-scoring services. <strong>Student data is NOT used to train content-processing models.</strong> the MOSAIC content-scoring vendor API terms prohibit training on customer data.</p>
      </div>
      <DT headers={['Automated features','Data Sent','Returns','Retained?']} rows={[
        ['Speaking Lab','Transcript (no name/ID)','Rubric scores, feedback','No'],
        ['Writing Portfolio','Writing text (no name/ID)','Scores, suggestions','No'],
        ['Vocab Builder','Grade, level, category','Word sets','No'],
        ['Lesson Plans','Grade, topic (no student data)','Lesson plan','No'],
        ['Parent Letters','Scores (no name)','Translated letter','No'],
        ['ELL Screener','Answers (no name/ID)','Placement level','No'],
        ['Exam Scoring','Response (no name/ID)','WIDA scores','No'],
        ['Text-to-Speech','Question text only','Audio file','No'],
      ]} />
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mt-2">
        <p className="text-[10px] text-amber-800"><strong>Safeguards:</strong> Names/IDs NEVER sent to any content-processing service. The content-scoring vendor prohibits training on API data. Automated features disableable per-school.</p>
      </div>
    </Section>

    <Section icon={Trash2} title="Retention & Deletion Policy" color="bg-amber-100 text-amber-700">
      <DT headers={['Category','Retention','Method','Trigger']} rows={[
        ['Student records','License + 90d','Permanent deletion','Expiry/request'],
        ['Accounts','License + 90d','Deactivate→delete','Expiry/request'],
        ['Responses','License + 90d','Cascade delete','Expiry/request'],
        ['Audio (temp)','< 5 minutes','Auto-delete after STT','Immediate'],
        ['Sessions','24 hours','Auto-purge','Automatic'],
        ['Audit logs','1 year','Automated purge','Automatic'],
      ]} />
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mt-2">
        <p className="text-[10px] text-amber-800"><strong>Deletion:</strong> Email support@mosaicassessmentco.com. Complete within 30 days. Certificate of Records Disposal provided.</p>
      </div>
    </Section>

    <Section icon={Globe} title="Subprocessor List" color="bg-teal-100 text-teal-700">
      <DT headers={['Subprocessor','Purpose','Data','Location','DPA?']} rows={[
        ['MongoDB Atlas','Database','All data (encrypted)','US-East','Yes'],
        ['the MOSAIC content-scoring vendor (Emergent)','Automated rubric support','Anonymized responses','US','Yes (no-train)'],
        ['SendGrid (Twilio)','Email','Recipient emails only','US','Yes'],
        ['Cloudflare','DNS/CDN','No student data','US','Yes'],
        ['Emergent Labs','Hosting','App runtime','US','Yes'],
      ]} />
    </Section>

    <Section icon={AlertTriangle} title="Incident Response Plan" color="bg-red-100 text-red-600">
      {[
        {p:'1',t:'Detection',time:'0-1hr',d:'Monitor alerts → triage scope and severity.'},
        {p:'2',t:'Containment',time:'1-4hr',d:'Isolate systems, revoke credentials, preserve evidence.'},
        {p:'3',t:'Notify NYCPS',time:'24hr',d:'ContractorDataSharing@schools.nyc.gov with full details.'},
        {p:'4',t:'Notify Schools',time:'48hr',d:'Plain-English notice to affected administrators.'},
        {p:'5',t:'Notify Parents',time:'72hr',d:'If required, in applicable languages.'},
        {p:'6',t:'Remediation',time:'5-15d',d:'RCA, patches, written report to NYCPS.'},
      ].map((s,i) => (
        <div key={i} className="flex gap-3 items-start">
          <div className="w-6 h-6 rounded-full bg-red-100 text-red-700 flex items-center justify-center text-[10px] font-bold flex-shrink-0">{s.p}</div>
          <div><span className="text-xs font-bold text-gray-800">{s.t}</span><span className="text-[10px] bg-gray-100 text-gray-600 px-1 py-0.5 rounded ml-1">{s.time}</span><p className="text-[10px] text-gray-600">{s.d}</p></div>
        </div>
      ))}
    </Section>

    <Section icon={Users} title="Parent-Facing Privacy Summary" color="bg-emerald-100 text-emerald-700">
      <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
        <p className="text-xs font-bold text-emerald-800 mb-2">Plain-Language Summary for Parents</p>
        {[
          'Your child\'s info is used ONLY for WIDA test practice.',
          'We collect only: name, grade, school, test answers. No address, phone, SSN.',
          'Data stored securely in the US with encryption.',
          'We NEVER sell or share data with advertisers.',
          'The content-scoring service sees only answers — never names or IDs.',
          'the MOSAIC content-scoring vendor cannot use your child\'s data for training.',
          'Data deleted within 90 days after license ends.',
          'You can review or request deletion anytime.',
          'Breach notification: school in 24hr, you in 72hr.',
        ].map((t,i) => (
          <div key={i} className="flex items-start gap-2 py-0.5"><CheckCircle size={11} className="text-emerald-600 mt-0.5 flex-shrink-0" /><p className="text-[11px] text-gray-700">{t}</p></div>
        ))}
      </div>
    </Section>

    <Section icon={Accessibility} title="Accessibility Statement" color="bg-sky-100 text-sky-700">
      <DT headers={['Feature','Implementation']} rows={[
        ['Keyboard Nav','All elements keyboard-accessible'],
        ['Screen Reader','Semantic HTML, ARIA labels'],
        ['Text Scaling','3-tier: K-2 large, 3-6 balanced, 7-12 standard'],
        ['Color Contrast','Min 4.5:1 (WCAG AA)'],
        ['Audio','TTS for Listening, adjustable playback'],
        ['Accommodations','Extended time, read-aloud, per-modality'],
        ['Multi-Language','Parent letters in 10 languages'],
      ]} />
    </Section>

    <Section icon={FileText} title="DPA Attachments A-E (Drafts)" color="bg-gray-100 text-gray-700">
      {[
        {t:'A — Services',d:'WIDA-aligned practice assessments K-12: digital (1,062 questions, 4 modalities), print booklets, MOSAIC Toolkit (6 tools), Lesson Plans, Quick Checks, WIDA analytics. RBAC for students, teachers, admins.'},
        {t:'B — Privacy & Security',d:'US-only (MongoDB Atlas US-East). AES-256 at rest, TLS 1.2+ in transit. RBAC 5 levels. bcrypt. 24hr sessions. Audit logging. no PII to content-processing services.'},
        {t:'C — Parent Bill of Rights',d:'See Parent Summary. Privacy Policy at mosaicassessmentco.com/privacy. Parents inspect, correct, delete via school or support@mosaicassessmentco.com.'},
        {t:'D — Third Party Security',d:'All subprocessors have DPAs. No independent PII storage. HTTPS-only, API auth, DB-level controls.'},
        {t:'E — Records Disposal',d:'On termination: deactivate (5d), export (15d), permanent delete (30d), signed Certificate of Disposal to admin + NYCPS.'},
      ].map((a,i) => (
        <div key={i} className="border border-gray-200 rounded-lg p-3"><h4 className="text-xs font-bold text-gray-800 mb-1">{a.t}</h4><p className="text-[11px] text-gray-600">{a.d}</p></div>
      ))}
    </Section>

    <Section icon={Key} title="Chancellor's Reg A-820 Alignment" color="bg-orange-100 text-orange-700">
      <DT headers={['Requirement','MOSAIC']} rows={[
        ['Written privacy policy','mosaicassessmentco.com/privacy'],
        ['Educational purpose only','Assessment practice only'],
        ['No sale of data','Never sold, rented, shared'],
        ['No advertising use','No ads, no profiling'],
        ['Security procedures','Encryption, RBAC, logging, brute-force'],
        ['Breach notification','24hr NYCPS, 48hr schools, 72hr parents'],
        ['Deletion on termination','30 days + Certificate of Disposal'],
        ['Subprocessor transparency','mosaicassessmentco.com/subprocessors'],
        ['Parent data access','Via school admin or direct request'],
        ['Automated processing protections','no PII to content-processing services, no model training'],
      ]} />
    </Section>

    {/* ═══ EXCEEDS ERMA: SOC 2 Readiness ═══ */}
    <div className="bg-amber-50 border-2 border-amber-300 rounded-lg p-3 mt-2">
      <p className="text-[10px] font-bold text-amber-800 uppercase tracking-wider">EXCEEDS ERMA MINIMUM</p>
    </div>

    <Section icon={Shield} title="SOC 2 Type II Readiness Self-Assessment" color="bg-amber-100 text-amber-700" defaultOpen>
      <p className="text-xs text-gray-500 mb-2">Mapped against AICPA Trust Service Criteria. MOSAIC is audit-ready across all 5 categories.</p>
      <DT headers={['Trust Service Criteria','Control','MOSAIC Status']} rows={[
        ['CC1 — Control Environment','Organizational commitment to integrity & ethics','CEO-led privacy culture, written policies, all staff trained'],
        ['CC2 — Communication','Internal/external privacy communication','Privacy Policy public, Parent Summary, Subprocessor list, ERMA tab'],
        ['CC3 — Risk Assessment','Identify & assess risks to data','Annual risk review, ERMA self-assessment, threat modeling for automated processing flows'],
        ['CC4 — Monitoring','Ongoing monitoring of controls','Real-time compliance dashboard (see below), audit logging, session monitoring'],
        ['CC5 — Control Activities','Policies & procedures enforced','RBAC, bcrypt, TLS, brute-force lockout, input validation, exam integrity'],
        ['CC6 — Logical Access','Access restricted to authorized users','5-tier RBAC, school_id isolation, 24hr sessions, API-level auth'],
        ['CC7 — System Operations','Systems monitored & maintained','Automated backups, uptime monitoring, hot-reload deployments, zero-downtime'],
        ['CC8 — Change Management','Changes controlled & tested','Version-controlled releases, changelog, testing before deploy'],
        ['CC9 — Risk Mitigation','Risks identified & mitigated','6-phase incident response, data encryption, no PII to content-processing services, US-only storage'],
        ['A1 — Availability','System available for operation','Cloud hosting with auto-scaling, MongoDB Atlas HA, Cloudflare DDoS protection'],
        ['C1 — Confidentiality','Confidential info protected','AES-256 at rest, TLS 1.2+ in transit, no data sharing, no advertising use'],
        ['PI1 — Processing Integrity','Processing is complete & accurate','WIDA scoring algorithms validated, System outputs teacher-reviewable, audit trail'],
        ['P1 — Privacy (Collection)','Data collected for stated purpose only','Assessment practice only — no secondary collection, no tracking, no profiling'],
        ['P2 — Privacy (Use/Retention)','Data used/retained per policy','License+90d retention, auto-deletion, Certificate of Disposal on termination'],
        ['P3 — Privacy (Disposal)','Data disposed of securely','30-day deletion window, cascade delete, backup purge, written confirmation'],
      ]} />
      <div className="bg-green-50 border border-green-200 rounded-lg p-3 mt-2">
        <p className="text-[10px] text-green-800"><strong>Assessment:</strong> MOSAIC meets or exceeds all 15 Trust Service Criteria. Formal SOC 2 Type II audit can be initiated with any qualified CPA firm. Current state: <strong>Audit-Ready</strong>.</p>
      </div>
    </Section>

    {/* ═══ EXCEEDS ERMA: Data Flow Diagrams ═══ */}
    <Section icon={Activity} title="Data Flow Diagrams" color="bg-sky-100 text-sky-700">
      <p className="text-xs text-gray-600 mb-3">Visual architecture showing how data moves through MOSAIC. ERMA requires descriptions — these diagrams exceed that.</p>

      {/* System Architecture Flow */}
      <div className="border border-gray-200 rounded-lg p-4 mb-3">
        <h4 className="text-xs font-bold text-gray-800 mb-3">System Architecture — Data Flow</h4>
        <div className="flex items-center justify-between gap-2 text-[10px]">
          {[
            { label: 'Student / Teacher', sub: 'Browser (HTTPS)', color: 'bg-blue-100 border-blue-300 text-blue-800' },
            { label: 'Cloudflare', sub: 'DDoS / TLS termination', color: 'bg-gray-100 border-gray-300 text-gray-700' },
            { label: 'React Frontend', sub: 'No data storage', color: 'bg-indigo-100 border-indigo-300 text-indigo-800' },
            { label: 'FastAPI Backend', sub: 'RBAC + validation', color: 'bg-green-100 border-green-300 text-green-800' },
            { label: 'MongoDB Atlas', sub: 'US-East, AES-256', color: 'bg-amber-100 border-amber-300 text-amber-800' },
          ].map((n, i) => (
            <React.Fragment key={i}>
              {i > 0 && <ArrowRight size={14} className="text-gray-400 flex-shrink-0" />}
              <div className={`border rounded-lg p-2 text-center flex-1 ${n.color}`}>
                <div className="font-bold">{n.label}</div>
                <div className="text-[9px] opacity-75">{n.sub}</div>
              </div>
            </React.Fragment>
          ))}
        </div>
        <p className="text-[10px] text-gray-500 mt-2 text-center">All connections encrypted (TLS 1.2+). No student data cached in browser. Sessions expire 24hr.</p>
      </div>

      {/* Automated Data Flow */}
      <div className="border border-purple-200 rounded-lg p-4">
        <h4 className="text-xs font-bold text-purple-800 mb-3">Automated Data Flow — What Goes to the MOSAIC content-scoring vendor</h4>
        <div className="flex items-center justify-between gap-2 text-[10px]">
          {[
            { label: 'Student Response', sub: 'Text only (no name/ID)', color: 'bg-blue-100 border-blue-300 text-blue-800' },
            { label: 'MOSAIC Backend', sub: 'Strips all PII', color: 'bg-green-100 border-green-300 text-green-800' },
            { label: 'the MOSAIC content-scoring vendor API', sub: 'Anonymized text + rubric', color: 'bg-purple-100 border-purple-300 text-purple-800' },
            { label: 'WIDA Feedback', sub: 'Scores returned', color: 'bg-amber-100 border-amber-300 text-amber-800' },
            { label: 'Stored in MOSAIC', sub: 'Linked to student', color: 'bg-green-100 border-green-300 text-green-800' },
          ].map((n, i) => (
            <React.Fragment key={i}>
              {i > 0 && <ArrowRight size={14} className="text-gray-400 flex-shrink-0" />}
              <div className={`border rounded-lg p-2 text-center flex-1 ${n.color}`}>
                <div className="font-bold">{n.label}</div>
                <div className="text-[9px] opacity-75">{n.sub}</div>
              </div>
            </React.Fragment>
          ))}
        </div>
        <div className="bg-red-50 border border-red-200 rounded p-2 mt-2">
          <p className="text-[10px] text-red-700 text-center font-bold">Student names, IDs, emails, and school info are NEVER transmitted to the MOSAIC content-scoring vendor. Only anonymized response text.</p>
        </div>
      </div>
    </Section>

    {/* ═══ EXCEEDS ERMA: Live Compliance Monitoring ═══ */}
    <ComplianceMonitoringSection />

    <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-center">
      <p className="text-xs text-gray-500">ERMA Compliance Center — MOSAIC by Lerno Co. — <strong>Exceeds ERMA Minimum Requirements</strong></p>
      <p className="text-[10px] text-gray-400 mt-1">Submit to ContractorDataSharing@schools.nyc.gov | support@mosaicassessmentco.com</p>
    </div>
  </div>
);

/* ═══ Live Compliance Monitoring (separate component for state) ═══ */
const ComplianceMonitoringSection = () => {
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);

  const fetchMetrics = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/compliance/monitoring`, authH());
      setMetrics(res.data);
    } catch { }
    finally { setLoading(false); }
  };

  const handleToggle = () => {
    if (!open && !metrics) fetchMetrics();
    setOpen(!open);
  };

  const M = ({ value, label, color = 'text-gray-800' }) => (
    <div className="bg-white border border-gray-200 rounded-lg p-3 text-center">
      <div className={`text-xl font-bold ${color}`}>{value}</div>
      <div className="text-[9px] text-gray-500 uppercase font-bold mt-0.5">{label}</div>
    </div>
  );

  return (
    <div className="bg-white border border-gray-200 rounded-lg overflow-hidden" data-testid="erma-compliance-monitoring">
      <button onClick={handleToggle} className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-green-100 text-green-700"><BarChart3 size={16} /></div>
          <span className="font-bold text-gray-800 text-sm">Live Compliance Monitoring</span>
          <span className="text-[10px] font-bold bg-green-100 text-green-700 px-2 py-0.5 rounded">LIVE</span>
        </div>
        {open ? <ChevronDown size={16} className="text-gray-400" /> : <ChevronRight size={16} className="text-gray-400" />}
      </button>
      {open && (
        <div className="px-4 pb-4 border-t border-gray-100 pt-3 space-y-3">
          {loading ? (
            <div className="py-6 text-center"><Loader2 className="animate-spin mx-auto text-green-600" size={20} /></div>
          ) : metrics ? (
            <>
              <p className="text-[10px] text-gray-500">Real-time metrics as of {new Date(metrics.generated_at).toLocaleString()}</p>
              <div className="grid grid-cols-5 gap-2">
                <M value={metrics.sessions?.active_24h || 0} label="Active Sessions (24h)" color="text-blue-600" />
                <M value={metrics.auth?.failed_logins_24h || 0} label="Failed Logins (24h)" color={metrics.auth?.failed_logins_24h > 10 ? 'text-red-600' : 'text-green-600'} />
                <M value={metrics.auth?.lockouts_7d || 0} label="Lockouts (7d)" color={metrics.auth?.lockouts_7d > 5 ? 'text-red-600' : 'text-green-600'} />
                <M value={metrics.assessments?.exams_30d || 0} label="Exams (30d)" color="text-indigo-600" />
                <M value={metrics.assessments?.violations_30d || 0} label="Integrity Alerts (30d)" color={metrics.assessments?.violations_30d > 0 ? 'text-amber-600' : 'text-green-600'} />
              </div>
              <div className="grid grid-cols-4 gap-2">
                <M value={metrics.ai?.total_30d || 0} label="Automated API Calls (30d)" color="text-purple-600" />
                <M value={metrics.governance?.breaches || 0} label="Breach Reports" color={metrics.governance?.breaches > 0 ? 'text-red-600' : 'text-green-600'} />
                <M value={metrics.schools?.licensed || 0} label="Licensed Schools" color="text-sky-600" />
                <M value={Object.values(metrics.users || {}).reduce((a,b)=>a+b,0)} label="Total Users" color="text-gray-700" />
              </div>
              <div className="grid grid-cols-4 gap-2">
                <M value={metrics.ai?.speaking || 0} label="Speaking Evals" color="text-sky-600" />
                <M value={metrics.ai?.writing || 0} label="Writing Evals" color="text-rose-600" />
                <M value={metrics.ai?.lessons || 0} label="Lesson Plans" color="text-indigo-600" />
                <M value={metrics.ai?.vocab || 0} label="Vocab Sets" color="text-purple-600" />
              </div>
              <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                <div className="flex items-center gap-2 mb-1"><CheckCircle size={14} className="text-green-600" /><span className="text-xs font-bold text-green-800">All Systems Compliant</span></div>
                <p className="text-[10px] text-green-700">Data residency: {metrics.governance?.residency} | Encryption: {metrics.governance?.encryption} | PII to content-processing services: NONE</p>
              </div>
              <button onClick={fetchMetrics} className="text-[10px] text-blue-600 hover:underline">Refresh metrics</button>
            </>
          ) : (
            <p className="text-xs text-gray-500 py-4 text-center">Click to load real-time compliance metrics</p>
          )}
        </div>
      )}
    </div>
  );
};

export default ERMAComplianceTab;
